import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Loading } from '../components/pages/Loading';
import { getWizardState, setWizardState } from '../storage/wizard-state';
import { getStaticData, StaticData } from '../api/static-data';

export interface WizardData { // TODO Define types
  solutions: string[];
  continent: string;
  countries: string[];
  countryTier: string;
  mainIndustry: string;
  industryRisk: string;
  subIndustry: string;
  customIndustry: string;
  licence: string;
  licenceRisk: string;
  companyType: string;
  companyYears: number;
  expectedMonthlyVolume: string;
  approvedProposal: string;
}

export enum WizardStep {
  HOME = '/',
  REGION = '/solutions',
  CONTINENT = '/continents',
  INDUSTRY = '/industry',
  LICENCE_TYPE = '/license-type',
  COMPANY_TYPE = '/company-type',
  EXPENDED_MONTHLY_VOLUME = '/monthly-volume',
  SORRY = '/sorry',
  PROPOSAL = '/proposal',
  PROPOSAL_APPROVAL = '/proposal-approval',
  LOADING = '/loading',
  APPROVAL_FORM = '/approval-form',
  OTHER_FORM = '/other-form',
  REJECTION_FORM = '/rejection-form',
  THANK_YOU = '/thank-you',
}

export interface WizardContextValues {
  loading: boolean;
  data: WizardData;
  currentStep: WizardStep;
  completedSteps: {
    [WizardStep.HOME]: boolean;
    [WizardStep.REGION]: boolean;
    [WizardStep.CONTINENT]: boolean;
    [WizardStep.INDUSTRY]: boolean;
    [WizardStep.LICENCE_TYPE]: boolean;
    [WizardStep.COMPANY_TYPE]: boolean;
    [WizardStep.EXPENDED_MONTHLY_VOLUME]: boolean;
    [WizardStep.SORRY]: boolean;
    [WizardStep.PROPOSAL]: boolean;
    [WizardStep.PROPOSAL_APPROVAL]: boolean;
    [WizardStep.LOADING]: boolean;
    [WizardStep.APPROVAL_FORM]: boolean;
    [WizardStep.OTHER_FORM]: boolean;
    [WizardStep.REJECTION_FORM]: boolean;
    [WizardStep.THANK_YOU]: boolean;
  }
  staticData: StaticData;
}

export interface WizardContextFunctions {
  changeStep: (step: WizardStep, fromSteps?: boolean, reload?: boolean) => void;
  updateStepData: (data: any) => void; // TODO
  resetState: () => void;
}

const defaultValues: WizardContextValues = {
  loading: false,
  data: {
    solutions: [],
    continent: '',
    countries: [],
    countryTier: '',
    mainIndustry: '',
    industryRisk: '',
    subIndustry: '',
    customIndustry: '',
    licence: '',
    licenceRisk: '',
    companyType: '',
    companyYears: 1,
    expectedMonthlyVolume: '',
    approvedProposal: ''
  },
  staticData: {
    solutions: [],
    countries: [],
    industries: [],
    licenses: []
  },
  currentStep: WizardStep.HOME,
  completedSteps: {
    [WizardStep.HOME]: false,
    [WizardStep.REGION]: false,
    [WizardStep.CONTINENT]: false,
    [WizardStep.INDUSTRY]: false,
    [WizardStep.LICENCE_TYPE]: false,
    [WizardStep.COMPANY_TYPE]: false,
    [WizardStep.EXPENDED_MONTHLY_VOLUME]: false,
    [WizardStep.LOADING]: false,
    [WizardStep.SORRY]: false,
    [WizardStep.PROPOSAL]: false,
    [WizardStep.PROPOSAL_APPROVAL]: false,
    [WizardStep.APPROVAL_FORM]: false,
    [WizardStep.OTHER_FORM]: false,
    [WizardStep.REJECTION_FORM]: false,
    [WizardStep.THANK_YOU]: false,
  }
};

const defaultFunctions: WizardContextFunctions = {
  changeStep: (step, fromSteps = false, reload = false) => undefined,
  updateStepData: (data) => undefined,
  resetState: () => undefined
};

export const WizardContext = React.createContext<WizardContextValues & WizardContextFunctions>({
  ...defaultValues,
  ...defaultFunctions
});

export const WizardContextProvider: FC = ({ children }) => {
  const storageState = getWizardState();

  const [state, setState] = useState<WizardContextValues>(storageState || defaultValues);

  const history = useHistory();

  useEffect(() => {
    (async () => {
      const staticData = await getStaticData();

      setState((state) => ({
        ...state,
        staticData
      }));
    })();
  }, []);

  useEffect(() => {
    history.push(state.currentStep);
  }, [state.currentStep, history]);

  useEffect(() => {
    setWizardState(state);
  });

  const changeStep = useCallback((step: WizardStep, fromSteps = false, reload = false) => {
    setState((state) => {
      let completedSteps = {
        ...state.completedSteps,
        [state.currentStep]: true
      };

      if (fromSteps) {
        completedSteps = state.completedSteps;
      }

      return {
        ...state,
        completedSteps,
        currentStep: step,
      };
    });

    // TODO - Revise Reload
    if (reload) {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }, [setState]);

  const updateStepData = useCallback((data) => {
    setState((state) => ({
      ...state,
      data: {
        ...state.data,
        ...data
      }
    }));
  }, [setState]);

  const resetState = useCallback(() => {
    setState(defaultValues);
  }, [setState]);

  const value: WizardContextValues & WizardContextFunctions = useMemo(() => ({
    ...state,
    changeStep,
    updateStepData,
    resetState
  }), [state, changeStep, updateStepData, resetState]);

  if (state.loading) {
    return (<Loading/>);
  }

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = () => {
  const context = useContext(WizardContext);

  if (!context) {
    throw new Error('You must use WizardContext inside WizardContextProvider.');
  }

  return context;
};
